import { DateTime } from "luxon"
import { LogError, LogWarning } from "./logger"

/**
 * =====================================================
 * UTILITIES
 * =====================================================
 */

const SHORT_NUM_DATE_TEST = /^\d{10}$/
const LONG_NUM_DATE_TEST = /^\d{13}$/
const GENERIC_NUM_DATE_TEST = /^\d{10}$|^\d{13}$/

export const DATE_SUBTYPES = {
  DATE: "date",
  TIME: "time",
  DATETIME: "datetime",
}

// Helper to double-check JS Date validity
function _isValidJsDate(dt) {
  // Convert the Luxon DateTime back to a JS Date to confirm if it's valid
  return dt.isValid && !isNaN(dt.toJSDate().getTime())
}

/**
 * Detects ISO datetime strings like:
 * 2026-02-09T20:45:00Z
 * 2026-02-09T20:45:00.000Z
 *
 * And numeric timestamps like:
 * 1676025600000 (10+ digit ms since epoch)
 * 1676025600 (10-digit seconds since epoch)
 *
 * Also accepts Date objects and numeric timestamps directly.
 *
 * Returns the type of date-like value ("date", "numeric", "string") for valid date-like values, null otherwise.
 */
function _isValidDateLikeValue(value) {
  // Date objects: check if it's a valid date (getTime() will return NaN for invalid dates)
  if (value instanceof Date) {
    return !isNaN(value.getTime())
  }

  // Numeric timestamps: check if it's a 10-digit seconds or 13-digit milliseconds timestamp,
  // and if it can be parsed into a valid date
  if (typeof value === "number") {
    const digits = String(Math.trunc(value)) // Remove any decimal part for the length check
    if (!GENERIC_NUM_DATE_TEST.test(digits)) return false

    const dt =
      digits.length === 10
        ? DateTime.fromSeconds(value)
        : DateTime.fromMillis(value)

    return dt.isValid && _isValidJsDate(dt)
  }

  // String values: check if it's a valid ISO datetime string, or a numeric timestamp string (seconds or milliseconds)
  if (typeof value === "string") {
    const s = value.trim()
    if (s.length === 0) return false

    // 10-digit numeric string (seconds since epoch)
    if (SHORT_NUM_DATE_TEST.test(s)) {
      const dt = DateTime.fromSeconds(Number(s))
      return dt.isValid && _isValidJsDate(dt)
    }

    // 13-digit numeric string (milliseconds since epoch)
    if (LONG_NUM_DATE_TEST.test(s)) {
      const dt = DateTime.fromMillis(Number(s))
      return dt.isValid && _isValidJsDate(dt)
    }

    // ISO datetime string: must contain "-" and "T" to be considered a valid ISO datetime
    if (!s.includes("-") && !s.includes("T") && !s.includes("t")) {
      return false
    }

    // Finally, check if it's a valid ISO datetime string
    return DateTime.fromISO(s).isValid
  }

  // Not a recognized date-like type
  return false
}

/**
 * Build a flat Map<field_name, fieldDef> from a fieldDefs array.
 * Only direct field defs from the provided list are indexed.
 * Nested payload traversal is handled generically by object/array walkers.
 */
export function buildFieldDefMap(fieldDefs, source = "buildFieldDefMap") {
  if (fieldDefs instanceof Map) {
    return fieldDefs
  }

  const map = new Map()

  if (!fieldDefs) {
    return map
  }

  if (!Array.isArray(fieldDefs)) {
    LogWarning(`${source}: Expected an array of field definitions.`)
    return map
  }

  for (const row of fieldDefs) {
    if (row?.field_name) {
      map.set(row.field_name, row)
    }
  }

  return map
}

// Helper to check if a value is a plain object (not an array, class instance, date, etc.)
function _isPlainObject(value) {
  return (
    value !== null &&
    typeof value === "object" &&
    Object.getPrototypeOf(value) === Object.prototype
  )
}

// Resolve the effective date subtype from a field definition.
export function resolveFieldSubtype(fieldDef) {
  const fieldType = fieldDef.field_type || null
  const rawSubtype = fieldDef.field_type_subtype || null

  // Handle "time" field_type...
  if (fieldType === "time") {
    // Return "time" for any field with field_type "time", regardless of subtype
    return DATE_SUBTYPES.TIME
  }

  // Handle "date" field_type...
  if (fieldType === "date") {
    if (
      rawSubtype === DATE_SUBTYPES.TIME ||
      rawSubtype === DATE_SUBTYPES.DATETIME
    ) {
      return rawSubtype
    }

    // Fall back to "date" if the subtype is not "time" or "datetime"
    return DATE_SUBTYPES.DATE
  }

  // Handle non-date field types with date subtypes
  // For example type:"sys", subtype:"date" - should still be treated as a date field
  if (
    [DATE_SUBTYPES.DATE, DATE_SUBTYPES.TIME, DATE_SUBTYPES.DATETIME].includes(
      rawSubtype,
    )
  ) {
    return rawSubtype
  }

  // For any other field_type, return null to indicate it's not a date field
  return null
}

/**
 * Parse an ISO datetime string (or date-only ISO) into a JavaScript Date in UTC.
 *
 * Accepts strings like:
 * - "2026-02-09T20:45:00Z"
 * - "2026-02-09T20:45:00.000Z"
 * - "2026-02-09"
 *
 * Returns a JS Date when parsing succeeds.
 * Falls back to `transformDate(value, "date", "date")` for non-ISO, partial,
 * or otherwise invalid inputs, which may return an empty string for falsy or
 * unparseable values.
 *
 * @param {string|number|Date|Object} value - date-like input value
 * @returns {Date|string}
 */
export function parseDate(value) {
  // Parse as ISO (in UTC)
  const parsedValue = DateTime.fromISO(value, { zone: "utc" })

  // If not a string, not a full ISO timestamp or not a valid date string, delegate to transformDate
  if (
    typeof value !== "string" ||
    value.trim().length < 19 ||
    !parsedValue.isValid
  ) {
    // For non-ISO or invalid date-like values, attempt to parse using transformDate's logic
    // Assume type:date, subtype:date - because ParseDate is meant for date values
    return transformDate(value, "date", "date")
  }

  // Convert to JS Date
  const parsedDate = parsedValue.toJSDate()

  return parsedDate
}

/**
 * =====================================================
 * NORMALIZATION
 * =====================================================
 */

/**
 * Convert a date-like value to a normalized string based on subtype.
 *
 * Accepts:
 * - Date objects
 * - ISO datetime strings (e.g. "2026-02-09T20:45:00Z")
 * - Numeric timestamps (milliseconds since epoch)
 *
 * Dates (and times) are normalized according to the subtype:
 *   - "date"     → "YYYY-MM-DD"
 *   - "time"     → "HH:mm:ss"  (e.g. "14:30:00")
 *   - "datetime" → ISO 8601  (e.g. "2026-04-16T14:30:00.000Z")
 *
 * Returns the original value unchanged if it cannot be parsed.
 */
function _normalizeDate(dateValue, subtype = DATE_SUBTYPES.DATE) {
  // If the value is not a valid date-like value, return it unchanged
  if (!_isValidDateLikeValue(dateValue)) return dateValue

  try {
    let dt

    // Date object: convert directly to Luxon DateTime
    if (dateValue instanceof Date) {
      dt = DateTime.fromJSDate(dateValue)
    }
    // Numeric timestamp: determine if it's seconds or milliseconds based on length, then convert to Luxon DateTime
    else if (typeof dateValue === "number") {
      const digits = String(Math.trunc(dateValue)) // Remove any decimal part for the length check
      dt =
        digits.length === 10
          ? DateTime.fromSeconds(dateValue)
          : DateTime.fromMillis(dateValue)
    }
    // String: check if it's a numeric timestamp (seconds or milliseconds), otherwise parse as ISO
    else {
      const stringValue = String(dateValue).trim()

      if (SHORT_NUM_DATE_TEST.test(stringValue)) {
        dt = DateTime.fromSeconds(Number(stringValue))
      } else if (LONG_NUM_DATE_TEST.test(stringValue)) {
        dt = DateTime.fromMillis(Number(stringValue))
      } else {
        dt = DateTime.fromISO(stringValue)
      }
    }

    // If parsing failed, return the original value unchanged
    if (!dt.isValid) return dateValue

    // Format the date according to the subtype
    switch (subtype) {
      case "time":
        return dt.toFormat("HH:mm:ss")
      case "datetime":
        return dt.toISO()
      default:
        return dt.toISODate()
    }
  } catch (error) {
    LogError("Unable to normalize date:", error)
    return dateValue
  }
}

/**
 * Normalize one value using its field definition.
 * - Date/time fields: normalize by subtype
 * - Everything else: return as-is
 */
function _normalizeValue(key, value, fieldDefMap) {
  const fieldDef = fieldDefMap.get(key)

  if (fieldDef !== undefined) {
    const resolvedSubtype = resolveFieldSubtype(fieldDef)

    // If this is a date/time field, normalize the value to the correct string format
    if (resolvedSubtype !== null) {
      return _normalizeDate(value, resolvedSubtype)
    }

    // Known non-date field — pass through as-is
    return value
  }

  // No field def found — pass through unchanged
  return value
}

/**
 * Normalize all key/value pairs in a plain object using the field def map.
 *
 * Keys with no field def are still traversed: plain-object values are descended
 * into (envelope wrappers like `values`), and arrays are mapped with recursion
 * into any plain-object elements. Only keys with a matching field def are converted.
 */
function _normalizeObject(obj, fieldDefMap) {
  const result = {}
  for (const [key, value] of Object.entries(obj)) {
    const hasFieldDef = fieldDefMap.has(key)

    if (!hasFieldDef && _isPlainObject(value)) {
      // Envelope wrapper (e.g. { values: { field: ... } }) — descend without a field def
      result[key] = _normalizeObject(value, fieldDefMap)
    } else if (Array.isArray(value)) {
      result[key] = value.map((row) =>
        _isPlainObject(row) ? _normalizeObject(row, fieldDefMap) : row,
      )
    } else {
      result[key] = _normalizeValue(key, value, fieldDefMap)
    }
  }
  return result
}

/**
 * Normalizes date fields in a form payload before submission.
 *
 * Uses field definitions from `fieldDefs` to determine which values should be
 * normalized.
 * Date-like fields are normalized using `field_type` and `field_type_subtype`:
 * - "date" (or null/missing) → "YYYY-MM-DD"
 * - "time"                   → "HH:mm:ss"  (e.g. "14:30:00")
 * - "datetime"               → ISO 8601  (e.g. "2026-04-16T14:30:00.000Z")
 *
 * Structural wrapper/container objects are traversed so normalization can reach
 * actual field keys nested under payload envelopes like `values`.
 * Fields without a matching definition pass through unchanged.
 *
 * @param {Object|Array} rawPayload
 * @param {Map|Array} fieldDefs
 * @returns {Object|Array}
 */
export function normalizePayload(rawPayload, fieldDefs) {
  try {
    // If the payload is empty, or not an object/array, return it unchanged (e.g. null, string, number)
    if (rawPayload === null || typeof rawPayload !== "object") return rawPayload

    const fieldDefMap = buildFieldDefMap(fieldDefs, "normalizePayload")

    // Array payload: normalize each item individually
    if (Array.isArray(rawPayload)) {
      return rawPayload.map((item) => {
        // If the item is a plain object, we can apply field defs to it
        if (item !== null && typeof item === "object") {
          return _normalizeObject(item, fieldDefMap)
        }

        // Item is not an object, so we can't apply field defs to it — return as-is
        return item
      })
    }

    // Single object payload
    const normalizedPayload = _normalizeObject(rawPayload, fieldDefMap)

    return normalizedPayload
  } catch (error) {
    LogError("Unable to normalize payload:", error)
    throw error
  }
}

/**
 * =====================================================
 * TRANSFORMATION
 * =====================================================
 */

/**
 * Parse a date field value from the server into a JavaScript Date object.
 *
 * Accepts Firestore timestamp objects (`{_seconds, _nanoseconds}`),
 * ISO date/time strings, time-only strings ("HH:mm:ss"), numeric timestamps
 * (ms) and Date instances.
 *
 * Behavior is driven by fieldType and fieldSubtype:
 * - fieldType === "time", or fieldSubtype === "time":
 *     time is preserved; "HH:mm:ss" strings are combined with today's local date.
 * - fieldSubtype === "datetime":
 *     full datetime is preserved.
 * - fieldSubtype === "date" or no recognized subtype:
 *     date-only; returned as JS Date at local noon to avoid DST edge cases.
 *
 * Falsy or unparseable inputs return an empty string.
 * Set returnLuxon to receive a Luxon DateTime instead of a JS Date.
 *
 * @param {Object|string|number|Date} fieldValue
 * @param {string} [fieldType]
 * @param {string} [fieldSubtype]
 * @param {boolean} [returnLuxon=false]
 * @returns {Date|string|DateTime}
 */
export function transformDate(
  fieldValue,
  fieldType,
  fieldSubtype,
  returnLuxon = false,
) {
  // Return empty string for falsy values (null, undefined, empty string)
  if (!fieldValue) return ""

  try {
    // Resolve effective subtype: fieldType="time" always wins; otherwise use
    // the explicit subtype if it's "time" or "datetime"; anything else defaults to "date".
    let effectiveSubtype = DATE_SUBTYPES.DATE
    if (fieldType === "time") {
      effectiveSubtype = DATE_SUBTYPES.TIME
    } else if (
      fieldSubtype === DATE_SUBTYPES.TIME ||
      fieldSubtype === DATE_SUBTYPES.DATETIME
    ) {
      effectiveSubtype = fieldSubtype
    }

    // keepTime >> drives whether we preserve the time component in the output
    const keepTime =
      effectiveSubtype === DATE_SUBTYPES.TIME ||
      effectiveSubtype === DATE_SUBTYPES.DATETIME

    // ################################################################
    // Try to parse the input value using the various accepted formats
    // ################################################################

    let formattedDate

    // Date objects
    if (fieldValue instanceof Date) {
      const dateVal = DateTime.fromJSDate(fieldValue)
      formattedDate = keepTime
        ? dateVal.toLocal().toISO()
        : dateVal.toLocal().toISODate()
    }
    // Firestore timestamp objects
    else if (
      typeof fieldValue === "object" &&
      "_seconds" in fieldValue &&
      "_nanoseconds" in fieldValue
    ) {
      const milliseconds =
        fieldValue._seconds * 1000 + fieldValue._nanoseconds / 1000000
      formattedDate = keepTime
        ? DateTime.fromMillis(milliseconds).toLocal().toISO()
        : DateTime.fromMillis(milliseconds).toLocal().toISODate()
    }
    // Numeric timestamps (number or numeric string)
    else if (
      (typeof fieldValue === "number" ||
        /^\d+$/.test(String(fieldValue).trim())) &&
      _isValidDateLikeValue(fieldValue)
    ) {
      const stringVal = String(fieldValue).trim()
      const numVal = Number(stringVal)
      const dateVal =
        stringVal.length === 10
          ? DateTime.fromSeconds(numVal)
          : DateTime.fromMillis(numVal)
      formattedDate = keepTime
        ? dateVal.toLocal().toISO()
        : dateVal.toLocal().toISODate()
    }
    // Time-only strings ("HH:mm:ss") — combined with today's local date
    // For this format, keepTime should be true, but we check it anyway
    else if (
      keepTime &&
      typeof fieldValue === "string" &&
      /^\d{2}:\d{2}:\d{2}$/.test(fieldValue.trim())
    ) {
      const parsed = DateTime.fromFormat(fieldValue.trim(), "HH:mm:ss")
      if (!parsed.isValid) return ""
      const today = DateTime.now()
      formattedDate = today
        .set({
          hour: parsed.hour,
          minute: parsed.minute,
          second: parsed.second,
          millisecond: 0,
        })
        .toISO()
    }
    // ISO strings or date-only strings
    else if (
      typeof fieldValue === "string" &&
      _isValidDateLikeValue(fieldValue)
    ) {
      const trimmedStringVal = fieldValue.trim()
      const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(trimmedStringVal)
      if (keepTime) {
        formattedDate = DateTime.fromISO(trimmedStringVal).toLocal().toISO()
      } else if (dateOnly) {
        formattedDate = trimmedStringVal
      } else {
        formattedDate = DateTime.fromISO(trimmedStringVal).toLocal().toISODate()
      }
    }
    // Unrecognized format — return empty string
    else {
      return ""
    }

    // #########################################################
    // Now that we have a normalized ISO string, parse it back
    // to a DateTime and return the appropriate output format
    // #########################################################

    if (keepTime) {
      // Return a JS Date (or Luxon DateTime) with the full time component intact
      const parsed = DateTime.fromISO(formattedDate)

      // Return a JS Date (or Luxon DateTime) with the time component preserved
      return returnLuxon ? parsed : parsed.toJSDate()
    } else {
      // Date-only: anchor to local noon so DST transitions can't shift the date
      // backward or forward when converting to a JS Date
      const parsed = DateTime.fromISO(formattedDate + "T12:00:00.000", {
        zone: "local",
      })

      // Return a JS Date (or Luxon DateTime) with the time set to local noon
      return returnLuxon ? parsed : parsed.toJSDate()
    }
  } catch (error) {
    // Log the error and return an empty string to avoid breaking the entire payload transformation
    LogError("Unable to transform date:", error)
    return ""
  }
}

/**
 * Transform a single payload value from the server using its field def.
 * - Has a recognized date subtype: convert via transformDate
 * - Known non-date field: pass through as-is
 * - No field def found: return as-is (cannot determine type without field def)
 */
function _transformValue(key, value, fieldDefMap) {
  // Skip empty/null values — nothing to transform
  if (value === null || value === undefined || value === "") return value

  const fieldDef = fieldDefMap.get(key)
  if (fieldDef !== undefined) {
    const resolvedSubtype = resolveFieldSubtype(fieldDef)

    // Date/time field: convert the raw server value to a JS Date via transformDate
    if (resolvedSubtype !== null) {
      return transformDate(value, fieldDef.field_type, resolvedSubtype)
    }

    // Known non-date field — pass through as-is
    return value
  }

  // No field def found — return as-is
  return value
}

/** Transform all key/value pairs in a plain object using the field def map. */
function _transformObject(obj, fieldDefMap) {
  const result = {}
  for (const [key, value] of Object.entries(obj)) {
    const hasFieldDef = fieldDefMap.has(key)

    if (!hasFieldDef && _isPlainObject(value)) {
      result[key] = _transformObject(value, fieldDefMap)
    } else if (Array.isArray(value)) {
      result[key] = value.map((row) =>
        _isPlainObject(row) ? _transformObject(row, fieldDefMap) : row,
      )
    } else {
      result[key] = _transformValue(key, value, fieldDefMap)
    }
  }
  return result
}

/**
 * Parses date field values in a server payload back into JavaScript Date objects
 * for use in forms.
 *
 * Conversion is driven by field_type and field_type_subtype from fieldDefs:
 * - "time" (field_type or subtype)  → JS Date with time preserved
 * - "datetime" subtype              → JS Date with time preserved
 * - "date" subtype or no subtype    → JS Date at local noon
 *
 * Fields without a recognized date subtype pass through unchanged.
 * Structural wrapper objects and arrays are traversed generically so nested
 * field keys can still be transformed.
 *
 * When fieldDefs is omitted, all fields pass through as-is.
 *
 * @param {Object|Array} rawPayload
 * @param {Map|Array|null} [fieldDefs]
 * @returns {Object|Array}
 */
export function transformPayload(rawPayload, fieldDefs) {
  try {
    // If the payload is empty, or not an object/array, return it unchanged (e.g. null, string, number)
    if (!rawPayload || typeof rawPayload !== "object") return rawPayload

    // Build a flat field-name → fieldDef lookup from field definitions
    const fieldDefMap = buildFieldDefMap(fieldDefs, "transformPayload")

    // Array payload: transform each item individually
    if (Array.isArray(rawPayload)) {
      return rawPayload.map((item) => {
        if (item !== null && typeof item === "object") {
          return _transformObject(item, fieldDefMap)
        }
        return item
      })
    }

    // Single object payload
    const transformedPayload = _transformObject(rawPayload, fieldDefMap)

    return transformedPayload
  } catch (error) {
    LogError("Unable to transform payload:", error)
    return null
  }
}
